import { device } from '@accedo/xdk-core'
import { ID as WEBOS } from '@accedo/xdk-device-lg-webos'
import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import { ID as TIZEN } from '@accedo/xdk-device-samsung-tizen'
import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import { ID as VIZIO } from '@accedo/xdk-device-vizio-smartcast-contrib'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'

import { Settings } from '@lightningjs/sdk'

const APP_NAME_LG = 'flosports-lg'
const APP_NAME_SAMSUNG = 'flosports-samsung'
const APP_NAME_VIZIO = 'flosports-vizio'
const APP_NAME_WORKSTATION = 'workstation'

export const getAppVersion = () => {
  return Settings.get('app', 'version', 'unknown')
}

export const getAppName = () => {
  if (isWebos) {
    return APP_NAME_LG
  } else if (isTizen) {
    return APP_NAME_SAMSUNG
  } else if (isVizio) {
    return APP_NAME_VIZIO
  } else {
    return APP_NAME_WORKSTATION
  }
}

export const getDeviceInfo = () => {
  // Prevent error when running on workstation
  if (device?.system?.getModel) {
    return {
      ASVersion: '',
      modelNumber: device?.system?.getModel(),
      serialNumber: '',
      versionNumber: '',
    }
  }

  const NAW = 'NA-workstation'

  return {
    ASVersion: NAW,
    hardwareName: NAW,
    modelNumber: NAW,
    serialNumber: NAW,
    versionNumber: NAW,
  }
}

export const getDeviceMake = () => {
  const platform = device.platform
  if (platform === TIZEN) {
    return 'samsung'
  } else if (platform === WEBOS) {
    return 'lg'
  } else if (platform === VIZIO) {
    return 'vizio'
  }

  return 'workstation'
}

export const getClientIp = async (): Promise<string> => {
  const { ip } = await fetch('https://api.ipify.org?format=json', {
    method: 'GET',
  })
    .then((res) => res.json())
    .catch((error) => console.error(error))

  return ip || '0.0.0.0'
}

export const isLowEndDevice = (): boolean => {
  if (window.tizen) {
    try {
      const tizenVersion = Number(
        tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version'),
      )

      return tizenVersion <= 6.0
    } catch (e) {
      console.error('There was a problem retrieving the Tizen OS version. Reason:', e)
      return undefined
    }
  }

  if (isWebos) {
    try {
      // Checking the device year based on the userAgent, from the official webOs developer portal:
      // https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine#useragent-string
      const maxOldChromiumVersion = 68
      const userAgent = navigator.userAgent

      const chromeMatch = userAgent.match(/Chrome\/(\d+)\./)

      if (chromeMatch && chromeMatch[1]) {
        const chromeVersion = parseInt(chromeMatch[1], 10)

        return chromeVersion <= maxOldChromiumVersion
      } else {
        return false
      }
    } catch (e) {
      console.error('There was a problem retrieving the WebOs version. Reason:', e)
      return undefined
    }
  }

  if (isVizio) {
    try {
      // We use this low end validation to render the canvas at 720p if needed
      // because the companion library might not be ready by then, we need to manually
      // check the model using the userAgent string provided by the browser which is always available
      const userAgent = navigator.userAgent

      return isLowEndVizio(userAgent)
    } catch (e) {
      console.error('There was a problem retrieving the Vizio series info. Reason:', e)
      return undefined
    }
  }
}

/**
 * Check if the Vizio model is a D series by checking the userAgent.
 * @param userAgent The user agent from the browser's TV
 * @returns True if the string includes Model/D, false otherwise.
 */
export const isLowEndVizio = (userAgent: string) => /Model\/D[A-Z0-9-]*/i.test(userAgent)

// Function to return whether or not we should get a manifest with no DVR enabled. This is primarily for low-end TVs that have trouble with a gowing manifest parse
// ACS-4411
// All LG and Vizio TVs are restricted from DVR
export const shouldDeviceHaveNoDVR = (): boolean => {
  if (!isWebos && !isVizio) return false

  return true
}
